import { Dayjs } from 'dayjs';

export type AppData = {
  contractStatus: ContractStatus[];
  contractInsuranceCoverageTypes: ContractInsuranceCoverageType[];
  counties: string[];
  countries: string[];
  crewMemberCertificateStatus: CrewMemberCertificateStatus[];
  crewMemberNoteType: CrewMemberNoteType[];
  crewMemberRole: CrewMemberRole[];
  crewMemberStatus: CrewMemberStatus[];
  errorType: ErrorType[];
  fuelTypes: string[];
  journeyNoteTypes: JourneyNoteType[];
  journeyReliefEventType: JourneyReliefEventType[];
  entityStatus: EntityStatus[];
  journeyStatus: JourneyStatus[];
  routeStatus: RouteStatus[];
  serviceTypes: ServiceType[];
  vesselCertificateStatus: VesselCertificateStatus[];
  vesselCertificateTypes: string[];
  vesselInsuranceCoverageTypes: VesselInsuranceCoverageType[];
  vesselNoteTypes: VesselNoteType[];
  vesselStatus: string[];
};

export interface SelectItem {
  label: string;
  value: string;
}

export interface AppFile {
  id: string;
  name: string;
  file: File | string;
}

export interface ApiFile {
  id: string;
  name: string;
  url: string;
}

export const mapFileToAppFile = (file: ApiFile): AppFile => ({
  id: file.id,
  name: file.name,
  file: file.url,
});

export enum ErrorType {
  GenericError = 'GenericError',
  ValueEmptyOrNull = 'ValueEmptyOrNull',
  ValueNull = 'ValueNull',
  ValueEmpty = 'ValueEmpty',
  ValidationError = 'ValidationError',
  WrongFormatEmail = 'WrongFormatEmail',
  OperatorMismatch = 'OperatorMismatch',
  NotFound = 'NotFound',
  CurrentUserNotFound = 'CurrentUserNotFound',
  ContractValidationError = 'ContractValidationError',
  BusinessRuleError = 'BusinessRuleError',
  NotEditTheirOwnProfile = 'NotEditTheirOwnProfile',
  NotActivatedUserOperator = 'NotActivatedUserOperator',
  EntityCosntructorError = 'EntityCosntructorError',
  ErrorToSaveInDB = 'ErrorToSaveInDB',
  WrongStatus = 'WrongStatus',
  ErrorInBlobStorage = 'ErrorInBlobStorage',
  DuplicateDefinition = 'DuplicateDefinition',
  InvalidJourneyLog = 'InvalidJourneyLog',
  InvalidCertificateCapacity = 'InvalidCertificateCapacity',
  InvalidVesselInsurance = 'InvalidVesselInsurance',
  InvalidJourneyContract = 'InvalidJourneyContract',
  InvalidJourneyRoute = 'InvalidJourneyRoute',
  EmptyVesselCertificate = 'EmptyVesselCertificate',
  InvalidJourneyVesselCertificate = 'InvalidJourneyVesselCertificate',
  EmptySkipperCertificate = 'EmptySkipperCertificate',
  InvalidJourneySkipperCertificate = 'InvalidJourneySkipperCertificate',
  InvoiceNotValidPeriod = 'InvoiceNotValidPeriod',
  InvoiceContractPeriodPendingApproval = 'InvoiceContractPeriodPendingApproval',
  InvoiceNotValidVessels = 'InvoiceNotValidVessels',
  MissingPublicAndEmployerLiabilityPolicy = 'MissingPublicAndEmployerLiabilityPolicy',
  InvoiceMissJourneys = 'InvoiceMissJourneys',
  InvoiceJourneysNotSubmitted = 'InvoiceJourneysNotSubmitted',
  NotJourneysToInvoice = 'NotJourneysToInvoice',
  InvoiceAlreadyExists = 'InvoiceAlreadyExists',
  RouteOperationFailed = 'RouteOperationFailed',
  WrongStatusForAction = 'WrongStatusForAction',
  CannotDeleteCertificate = 'CannotDeleteCertificate',
  NotASkipper = 'NotASkipper',
  CSVThrottle = 'CSVThrottle',
  DuplicatedOperatorName = 'DuplicatedOperatorName',
  FileOperationFailed = 'FileOperationFailed',
  ImmutableFields = 'ImmutableFields',
  DuplicateInvoiceNumber = 'DuplicateInvoiceNumber',
  OperationFailed = 'OperationFailed',
  ValidationErrors = 'ValidationErrors',
  JourneyDateOutOfRange = 'JourneyDateOutOfRange',
  DuplicateJourneyForDateRoute = 'DuplicateJourneyForDateRoute',
  NoPatchData = 'NoPatchData',
  NoPrimaryVessel = 'NoPrimaryVessel',
  CancelJourneyNoteRequired = 'CancelJourneyNoteRequired',
  NotAdHocJourney = 'NotAdHocJourney',
  PortNotAllowed = 'PortNotAllowed',
  ContractDeleteBlocked = 'ContractDeleteBlocked',
  DuplicatedContractName = 'DuplicatedContractName',
  DuplicateVesselRegNo = 'DuplicateVesselRegNo',
  FutureJourneyActionNotAllowed = 'FutureJourneyActionNotAllowed',
  OperatorInactive = 'OperatorInactive',
  SelfRoleChangeNotAllowed = 'SelfRoleChangeNotAllowed',
  DuplicatedUserEmail = 'DuplicatedUserEmail',
}

export enum AllowedCalendarRangeTypes {
  Monthly = 'Monthly',
  Daily = 'Daily',
}

export enum AllowedCalendarView {
  ListDay = 'listDay',
  ListMonth = 'listMonth',
  DayGridMonth = 'dayGridMonth',
}

export interface SessionCalendarState {
  selectedContractId: string | null;
  selectedDate: Dayjs;
  selectedView: AllowedCalendarView;
}

export interface ReasonNote<T> {
  id: string;
  note: string;
  creationDate: string;
  type: T;
}

export enum ContractStatus {
  Active = 'Active',
}

export enum ContractInsuranceCoverageType {
  PublicLiability = 'PublicLiability',
  EmployerLiability = 'EmployerLiability',
  All = 'All',
}

export enum CrewMemberCertificateStatus {
  PendingApproval = 'PendingApproval',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum CrewMemberNoteType {
  Rejected = 'Rejected',
}

export enum CrewMemberRole {
  Skipper = 'Skipper',
  Crew = 'Crew',
}

export enum CrewMemberStatus {
  Compliant = 'Compliant',
  Uncompliant = 'Uncompliant',
}

export enum JourneyStatus {
  AsScheduled = 'AsScheduled',
  Rescheduled = 'Rescheduled',
  Cancelled = 'Cancelled',
  ReliefEvent = 'ReliefEvent',
}

export enum JourneyReliefEventType {
  Weather = 'Weather',
  Tide = 'Tide',
  Swell = 'Swell',
  Mechanical = 'Mechanical',
  Other = 'Other',
}

export enum EntityStatus {
  NotLogged = 'NotLogged',
  Logged = 'Logged',
  Submitted = 'Submitted',
  Invoiced = 'Invoiced',
  Rejected = 'Rejected',
  PendingApproval = 'PendingApproval',
}

export enum JourneyNoteType {
  ChangeTime = 'ChangeTime',
  ChangePort = 'ChangePort',
  ChangeTimeAndPort = 'ChangeTimeAndPort',
  Cancel = 'Cancel',
  ReliefEvent = 'ReliefEvent',
  Rejected = 'Rejected',
}

export enum RouteStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export enum ServiceType {
  FerryPassenger = 'FerryPassenger',
  FerryRollOnRollOff = 'FerryRollOnRollOff',
  FerryPassengerAndLightCargo = 'FerryPassengerAndLightCargo',
  FastFerry = 'FastFerry',
  FerryHeavyCargo = 'FerryHeavyCargo',
  AirService = 'AirService',
  FacilityManagement = 'FacilityManagement',
}

export enum VesselCertificateStatus {
  Approved = 'Approved',
  Rejected = 'Rejected',
  PendingApproval = 'PendingApproval',
}

export enum VesselNoteType {
  Rejected = 'Rejected',
}

export enum VesselInsuranceCoverageType {
  VesselInsurance = 'VesselInsurance',
}

export enum VesselStatus {
  Compliant = 'Compliant',
  Uncompliant = 'Uncompliant',
}
